import React, { useRef, useMemo, useCallback, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Undo as UndoIcon, Redo as RedoIcon } from '@mui/icons-material';
import { blogService } from '../services/blogService';

interface BlogEditorProps {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
}

const BlogEditor: React.FC<BlogEditorProps> = ({ value, onChange, maxLength = 20000 }) => {
  const quillRef = useRef<ReactQuill | null>(null);
  const lastEmittedRef = useRef(value);
  const syncingRef = useRef(false);
  const loadedLenRef = useRef(0);

  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    lastEmittedRef.current = value;
    const quill = quillRef.current?.getEditor() as any;
    if (!quill) return;
    const sel = quill.getSelection();
    syncingRef.current = true;
    try {
      const delta = quill.clipboard.convert(value || '');
      quill.setContents(delta, 'silent');
      lastEmittedRef.current = quill.root.innerHTML;
      loadedLenRef.current = Math.max(loadedLenRef.current, quill.getLength());
      if (sel) quill.setSelection(sel);
    } finally {
      syncingRef.current = false;
    }
  }, [value]);

  const handleChange = useCallback((content: string, _delta: any, source: string) => {
    if (syncingRef.current || source !== 'user') return;
    const quill = quillRef.current?.getEditor() as any;
    let html = content;
    const limit = Math.max(maxLength, loadedLenRef.current) + 1;
    if (quill && quill.getLength() > limit) {
      quill.deleteText(limit, quill.getLength() - limit, 'silent');
      html = quill.root.innerHTML;
    }
    lastEmittedRef.current = html;
    onChange(html);
  }, [onChange, maxLength]);

  const imageHandler = useCallback(() => {
    const quill = quillRef.current?.getEditor() as any;
    if (!quill) return;
    const contents = quill.getContents();
    let imageCount = 0;
    contents.ops?.forEach((op: any) => { if (op.insert && typeof op.insert === 'object' && op.insert.image) imageCount++; });
    if (imageCount >= 3) { alert('Не более 3 изображений на статью'); return; }
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/jpeg,image/png,image/gif,image/webp');
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 800 * 1024) { alert('Размер файла не более 800 КБ'); return; }
      try {
        const result = await blogService.uploadImage(file);
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, 'image', result.url, 'user');
        quill.setSelection({ index: range.index + 1, length: 0 });
      } catch { alert('Ошибка загрузки изображения'); }
    };
    input.click();
  }, []);

  const handleUndo = () => { (quillRef.current?.getEditor() as any)?.history?.undo(); };
  const handleRedo = () => { (quillRef.current?.getEditor() as any)?.history?.redo(); };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'code-block'],
        ['image'],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['clean'],
      ],
      handlers: { image: imageHandler },
    },
    history: { delay: 500, maxStack: 200, userOnly: true },
  }), [imageHandler]);

  const formats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'blockquote', 'code-block',
    'image', 'indent', 'align',
  ];

  return (
    <div>
      <style>{`
        .ql-toolbar.ql-snow { border:1px solid #e2e8f0 !important; border-radius:0 !important; background:#f8fafc !important; padding:4px 8px !important; }
        .ql-container.ql-snow { border:1px solid #e2e8f0 !important; border-top:none !important; border-radius:0 0 8px 8px !important; min-height:300px; font-family:inherit !important; font-size:0.9rem !important; }
        .ql-editor { min-height:300px; line-height:1.7; padding:12px !important; }
        .ql-editor.ql-blank::before { font-style:normal !important; color:#94a3b8 !important; }
        .ql-snow .ql-tooltip { z-index:10000; }
      `}</style>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderBottom: 'none',
        borderRadius: '8px 8px 0 0', px: 1, py: 0.5,
      }}>
        <Tooltip title="Отменить (Ctrl+Z)">
          <IconButton size="small" onClick={handleUndo} sx={{ width: 32, height: 32, borderRadius: 1, color: '#475569', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <UndoIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Повторить (Ctrl+Shift+Z)">
          <IconButton size="small" onClick={handleRedo} sx={{ width: 32, height: 32, borderRadius: 1, color: '#475569', '&:hover': { bgcolor: '#e2e8f0' } }}>
            <RedoIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <ReactQuill ref={quillRef} theme="snow" defaultValue={value} onChange={handleChange} modules={modules} formats={formats} placeholder="Напишите статью..." />
    </div>
  );
};

export default BlogEditor;
